import { useNavigate } from "react-router-dom"
import { Box, Button, Chip, Stack, Typography } from "@mui/material"
import { AccountBalance, CheckCircle, LocalPhone, ShieldOutlined } from "@mui/icons-material"

import { PHONE_HREF } from "../constants/contactInfo"
import { homeCopy } from "../content/homeCopy"
import {
    availabilityLabel,
    availabilityChipSx,
    formatVehicleWeeklyPriceLabel,
    getHirePackages,
    getVehicleAvailability,
    getVehicleColour,
    getVehicleCoverUrl,
    getVehicleDisplayName,
    getVehicleFuel,
    getVehicleLicensingAuthority,
    getVehicleSeats,
    getVehicleTags,
    getVehicleTransmission,
} from "../utils/vehicleHelpers"
import { useEnquire } from "./EnquireModal"

const makeStyles = () => ({
    card: {
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        p: 0,
        overflow: "hidden",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "rgba(0, 4, 53, 0.12)",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
            boxShadow: "0 12px 32px rgba(0, 4, 53, 0.1)",
            transform: "translateY(-2px)",
        },
    },
    media: (image) => ({
        position: "relative",
        width: "100%",
        paddingBottom: "58%",
        backgroundImage: image
            ? `linear-gradient(180deg, transparent 55%, rgba(0,4,53,0.08)), url('${image}')`
            : "linear-gradient(180deg, #eef2f7, #dfe6ef)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "#eef2f7",
    }),
    badges: {
        position: "absolute",
        top: 12,
        left: 12,
        right: 12,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 1,
        pointerEvents: "none",
    },
    body: {
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        flex: 1,
        minWidth: 0,
    },
    actions: {
        mt: "auto",
        pt: 0.5,
        ".MuiButton-root": {
            flex: 1,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 700,
        },
    },
})

const CarCard = ({ vehicle, aos, aosDelay }) => {
    const styles = makeStyles()
    const navigate = useNavigate()
    const openEnquire = useEnquire()

    const _id = vehicle?._id
    const detailPath = _id ? `/vehicles/${_id}` : "/vehicles"
    const coverUrl = getVehicleCoverUrl(vehicle)
    const name = getVehicleDisplayName(vehicle)
    const priceLabel = formatVehicleWeeklyPriceLabel(vehicle)
    const packages = getHirePackages(vehicle)
    const available = getVehicleAvailability(vehicle)
    const vehicleTags = getVehicleTags(vehicle)
    const licensingAuthority = getVehicleLicensingAuthority(vehicle)

    const insuranceNote = (() => {
        if (packages.withInsurance && packages.withoutInsurance) {
            return "With or without insurance"
        }
        if (packages.withInsurance) return "Insurance included in weekly price"
        if (packages.withoutInsurance) return "Insurance not included in weekly price"
        return null
    })()
    const insurancePositive = Boolean(packages.withInsurance)

    const meta = [getVehicleColour(vehicle), getVehicleFuel(vehicle), getVehicleTransmission(vehicle), getVehicleSeats(vehicle) ? `${getVehicleSeats(vehicle)} seats` : null]
        .filter(Boolean)
        .join(" · ")

    const isAvailable = available === 1

    const openDetail = () => {
        if (_id) navigate(detailPath)
    }

    const stopCardNavigation = (event) => {
        event.stopPropagation()
    }

    return (
        <Box
            component="article"
            sx={styles.card}
            data-aos={aos}
            data-aos-duration="1000"
            data-aos-delay={aosDelay || undefined}
            onClick={openDetail}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    openDetail()
                }
            }}
            role="link"
            tabIndex={0}
            aria-label={`View details for ${name}`}
        >
            <Box sx={styles.media(coverUrl)}>
                <Box sx={styles.badges}>
                    <Chip
                        size="small"
                        label={availabilityLabel(available, vehicle.available_from)}
                        sx={{
                            fontWeight: 700,
                            ...availabilityChipSx(available),
                        }}
                    />
                    {vehicleTags.length > 0 && (
                        <Stack direction="column" alignItems="flex-end" spacing={0.5} sx={{ maxWidth: "58%" }}>
                            {vehicleTags.map((tag) => (
                                <Chip
                                    key={tag}
                                    size="small"
                                    label={tag}
                                    sx={{
                                        fontWeight: 700,
                                        height: "auto",
                                        py: 0.35,
                                        bgcolor: "rgba(255,255,255,0.95)",
                                        color: "#000435",
                                        "& .MuiChip-label": {
                                            whiteSpace: "normal",
                                            textAlign: "right",
                                            lineHeight: 1.25,
                                        },
                                    }}
                                />
                            ))}
                        </Stack>
                    )}
                </Box>
            </Box>

            <Box sx={styles.body}>
                <Typography variant="h6" fontWeight={800} color="#000435" lineHeight={1.25}>
                    {name}
                </Typography>

                {meta && (
                    <Typography variant="body2" color="text.secondary">
                        {meta}
                    </Typography>
                )}

                {licensingAuthority ? (
                    <Chip
                        icon={<AccountBalance sx={{ fontSize: "18px !important" }} />}
                        label={licensingAuthority}
                        size="small"
                        sx={{
                            alignSelf: "flex-start",
                            fontWeight: 700,
                            height: "auto",
                            py: 0.5,
                            bgcolor: "rgba(0, 163, 255, 0.12)",
                            color: "#000435",
                            border: "1px solid rgba(0, 163, 255, 0.22)",
                            "& .MuiChip-label": {
                                whiteSpace: "normal",
                                lineHeight: 1.3,
                            },
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        Licensing authority not listed
                    </Typography>
                )}

                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <CheckCircle sx={{ fontSize: 18, color: "#00B67A" }} />
                    <Typography variant="body2" fontWeight={600} color="#000435">
                        {homeCopy.carCard.hireBadge}
                    </Typography>
                </Stack>

                <Stack spacing={0.5}>
                    {priceLabel && (
                        <Stack direction="row" alignItems="baseline" spacing={0.75} flexWrap="wrap">
                            {priceLabel.startsWith("from ") && (
                                <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                    from
                                </Typography>
                            )}
                            <Typography variant="h5" fontWeight={800} color="#000435">
                                {priceLabel.replace(/^from /, "")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                per week
                            </Typography>
                        </Stack>
                    )}

                    {packages.withInsurance && packages.withoutInsurance && (
                        <Typography variant="caption" color="text.secondary">
                            £{packages.withInsurance.weekly.toFixed(0)} with insurance · £
                            {packages.withoutInsurance.weekly.toFixed(0)} without
                        </Typography>
                    )}

                    {insuranceNote && (
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <ShieldOutlined
                                sx={{
                                    fontSize: 17,
                                    color: insurancePositive ? "#00B67A" : "text.secondary",
                                }}
                            />
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                color={insurancePositive ? "#000435" : "text.secondary"}
                            >
                                {insuranceNote}
                            </Typography>
                        </Stack>
                    )}
                </Stack>

                <Stack direction="row" spacing={1} sx={styles.actions} onClick={stopCardNavigation}>
                    <Button
                        size="large"
                        variant="contained"
                        color="secondary"
                        onClick={() => openEnquire?.({ carName: name })}
                    >
                        Enquire now
                    </Button>
                    <Button
                        size="large"
                        variant="outlined"
                        color="primary"
                        component="a"
                        href={PHONE_HREF}
                        startIcon={<LocalPhone />}
                    >
                        Call
                    </Button>
                </Stack>
            </Box>
        </Box>
    )
}

export default CarCard
